import { Link, NavLink } from 'react-router-dom'
import { useState } from 'react'
import { clsx } from 'clsx'
import { MapPin, Phone, ShoppingCart, Menu, X } from 'lucide-react'
import { STORE } from '@/config/store'
import { useCartStore } from '@/store/useCartStore'

const NAV = [
  { to: '/', label: 'Shop', end: true },
  { to: '/about', label: 'About', end: false },
  { to: '/contact', label: 'Contact', end: false },
]

/**
 * Marine Tackle storefront header — a bespoke shop chrome that replaces the
 * iSQROLL marketplace header. Hi-vis utility strip (contact + trust signals)
 * over a clean main bar. Cart is always available; account is optional
 * (guests can shop and check out without signing in).
 */
export function StorefrontHeader() {
  const { itemCount, openDrawer: openCart } = useCartStore()
  const [menuOpen, setMenuOpen] = useState(false)

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      'font-display uppercase tracking-wide text-[19px] leading-none transition-colors',
      isActive ? 'text-primary-600' : 'text-ink hover:text-primary-600'
    )

  return (
    <header className="sticky top-0 z-40">
      {/* Utility strip */}
      <div className="bg-ink text-white/70">
        <div className="container flex h-9 items-center justify-between text-[13px]">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary-400" />
              {STORE.location.replace(', New Zealand', '')}
            </span>
            <a href={`tel:${STORE.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 hover:text-white">
              <Phone className="w-3.5 h-3.5 text-primary-400" />
              {STORE.phone}
            </a>
          </div>
          <div className="hidden sm:flex items-center gap-5 font-display uppercase tracking-wide">
            <span>Ships NZ-wide</span>
            <span className="text-primary-400">Official Yamaha &amp; Can-Am Dealer</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex-shrink-0" aria-label={STORE.name}>
            <img src={STORE.assets.logo} alt={STORE.legalName} className="h-11 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={navLinkClass}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            <button onClick={openCart} className="btn-ghost p-2 relative" aria-label="Cart">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-primary-600 text-white text-xs font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
                  aria-label={`${itemCount} items in cart`}
                >
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden btn-ghost p-2"
              aria-label="Menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-[1100] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <nav className="absolute right-0 top-0 h-full w-72 max-w-full bg-white shadow-xl flex flex-col animate-slide-up">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-ink">
              <img src={STORE.assets.logoSmall} alt={STORE.name} className="h-8 w-auto" />
              <button onClick={() => setMenuOpen(false)} className="p-2 text-white/80 hover:text-white" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 px-3 rounded-lg font-display uppercase tracking-wide text-xl text-ink hover:bg-slate-50"
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
            <div className="p-4 border-t border-slate-100 text-sm text-slate-500">
              <a href={`tel:${STORE.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-primary-600" /> {STORE.phone}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
